import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router";
import {
  CATEGORY_LABEL,
  EXPENSE_CATEGORIES,
  formatCents,
  type DashboardData,
  type ExpenseInfo,
  type ExpenseListResponse,
  type MeResponse,
} from "@handvero/shared";
import { api, errorText, upload } from "../api";
import { eurosToCents } from "./Customers";
import { Icon, Message, Sheet, useUnauthorizedRedirect } from "./ui";
import "./customers.css";
import "./documents.css";
import "./bookkeeping.css";

/*
 * Übersicht (Dashboard) und Ausgaben mit Belegen. Die Übersicht ist anpassbar: Kacheln ein- und ausblenden und verschieben,
 * die Auswahl merkt sich der Browser pro Person.
 */

const centsToText = (c: number) => (c / 100).toFixed(2).replace(".", ",");
const dateText = (d: string | null) => (d ? d.split("-").reverse().join(".") : "");
const MONTHS = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

// ---------- Übersicht ----------

type WidgetId = "kpi" | "offen" | "belege" | "monate" | "zahlungen" | "kunden" | "grenzen";
const WIDGETS: [WidgetId, string][] = [
  ["kpi", "Kennzahlen des Jahres"],
  ["offen", "Offene Rechnungen"],
  ["belege", "Fehlende Belege"],
  ["monate", "Verlauf nach Monaten"],
  ["zahlungen", "Letzte Zahlungseingänge"],
  ["kunden", "Beste Kunden"],
  ["grenzen", "Kleinunternehmer-Grenzen"],
];
const STORE = "hv-dashboard-v1";

interface Layout {
  order: WidgetId[];
  hidden: WidgetId[];
}

function readLayout(): Layout {
  const base: Layout = { order: WIDGETS.map(([id]) => id), hidden: [] };
  try {
    const raw = JSON.parse(localStorage.getItem(STORE) ?? "null") as Layout | null;
    if (raw && Array.isArray(raw.order) && Array.isArray(raw.hidden)) {
      const known = raw.order.filter((id) => WIDGETS.some(([w]) => w === id));
      return { order: [...known, ...base.order.filter((id) => !known.includes(id))], hidden: raw.hidden.filter((id) => WIDGETS.some(([w]) => w === id)) };
    }
  } catch {
    /* ohne Speicher gilt die Standardansicht */
  }
  return base;
}

export function DashboardView({ me }: { me: MeResponse }) {
  const onAuth = useUnauthorizedRedirect();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState("");
  const [layout, setLayout] = useState<Layout>(readLayout);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    api
      .get<DashboardData>(`/api/billing/dashboard?year=${year}`)
      .then((d) => {
        setData(d);
        setError("");
      })
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [year, onAuth]);

  function change(next: Layout) {
    setLayout(next);
    try {
      localStorage.setItem(STORE, JSON.stringify(next));
    } catch {
      /* nicht speicherbar, gilt nur für diese Sitzung */
    }
  }
  const move = (id: WidgetId, dir: -1 | 1) => {
    const i = layout.order.indexOf(id);
    const j = i + dir;
    if (j < 0 || j >= layout.order.length) return;
    const order = [...layout.order];
    [order[i], order[j]] = [order[j]!, order[i]!];
    change({ ...layout, order });
  };
  const toggle = (id: WidgetId) => change({ ...layout, hidden: layout.hidden.includes(id) ? layout.hidden.filter((h) => h !== id) : [...layout.hidden, id] });

  if (me.user.role === "employee") return <p className="cu-empty">Die Übersicht sehen nur Inhaber und Admins.</p>;
  const shown = layout.order.filter((id) => editing || !layout.hidden.includes(id));

  return (
    <section className="cu-panel">
      <div className="cu-bar">
        <div className="cu-roles" role="tablist" aria-label="Jahr">
          {[thisYear, thisYear - 1, thisYear - 2].map((y) => (
            <button key={y} className={year === y ? "on" : undefined} onClick={() => setYear(y)}>
              {y}
            </button>
          ))}
        </div>
        <span className="bk-export">
          <small>Für den Steuerberater ({year}):</small>
          <a className="dbtn ghost small" href={`/api/billing/export?year=${year}&kind=euer`}>
            Einnahmen-Überschuss
          </a>
          <a className="dbtn ghost small" href={`/api/billing/export?year=${year}&kind=einnahmen`}>
            Einnahmen
          </a>
          <a className="dbtn ghost small" href={`/api/billing/export?year=${year}&kind=ausgaben`}>
            Ausgaben
          </a>
          <a className="dbtn ghost small" href="/api/billing/verfahrensdokumentation" title="Muster zum Ausfüllen, von der Steuerberatung zu prüfen">
            Verfahrensdokumentation
          </a>
        </span>
        <button className={`dbtn ghost${editing ? " on" : ""}`} onClick={() => setEditing((e) => !e)}>
          <Icon name="settings" /> {editing ? "Fertig" : "Anpassen"}
        </button>
      </div>
      <Message>{error}</Message>
      {!data && !error && <p className="muted-s">Lädt …</p>}
      {data && (
        <div className="bk-grid">
          {shown.map((id) => {
            const w = WIDGETS.find(([x]) => x === id)!;
            if (id === "grenzen" && !data.limits) return null;
            const hidden = layout.hidden.includes(id);
            return (
              <article key={id} className={`bk-card ${id}${hidden ? " off" : ""}`}>
                <header>
                  <h3>{w[1]}</h3>
                  {editing && (
                    <span className="bk-edit">
                      <button onClick={() => move(id, -1)} aria-label="Nach vorn">
                        ↑
                      </button>
                      <button onClick={() => move(id, 1)} aria-label="Nach hinten">
                        ↓
                      </button>
                      <label>
                        <input type="checkbox" checked={!hidden} onChange={() => toggle(id)} /> zeigen
                      </label>
                    </span>
                  )}
                </header>
                {!hidden && <Widget id={id} d={data} />}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, tone, hint }: { label: string; value: string; tone?: "good" | "bad"; hint?: string }) {
  return (
    <div className={`bk-stat${tone ? ` ${tone}` : ""}`}>
      <small>{label}</small>
      <b>{value}</b>
      {hint && <em>{hint}</em>}
    </div>
  );
}

function Widget({ id, d }: { id: WidgetId; d: DashboardData }) {
  if (id === "kpi") {
    return (
      <div className="bk-stats">
        <Stat label="Umsatz (Rechnungen)" value={formatCents(d.revenueCents)} hint="ausgestellt, ohne Steuer" />
        <Stat label="Einnahmen (bezahlt)" value={formatCents(d.incomeCents)} hint="Geld ist eingegangen" />
        <Stat label="Ausgaben" value={formatCents(d.expensesCents)} hint={d.smallBusiness ? "mit Umsatzsteuer" : "ohne Vorsteuer"} />
        <Stat label="Gewinn" value={formatCents(d.profitCents)} tone={d.profitCents >= 0 ? "good" : "bad"} hint="Einnahmen minus Ausgaben" />
      </div>
    );
  }
  if (id === "offen") {
    return (
      <div className="bk-stats">
        <Stat label="Noch offen" value={formatCents(d.openCents)} hint="noch im Zahlungsziel" />
        <Stat label="Überfällig" value={formatCents(d.overdueCents)} tone={d.overdueCents > 0 ? "bad" : undefined} hint={d.overdueCount ? `${d.overdueCount} Rechnung${d.overdueCount === 1 ? "" : "en"}` : "nichts überfällig"} />
        {d.draftCount > 0 && <Stat label="Entwürfe" value={String(d.draftCount)} hint="noch nicht festgeschrieben" />}
        {d.overdueCount > 0 && (
          <Link className="dbtn small" to="/desktop/belege/mahnungen">
            Zum Mahnwesen
          </Link>
        )}
      </div>
    );
  }
  if (id === "belege") {
    return d.missingReceipts === 0 ? (
      <p className="muted-s">Zu allen Ausgaben liegt ein Beleg vor.</p>
    ) : (
      <div className="bk-stats">
        <Stat label="Ausgaben ohne Beleg" value={String(d.missingReceipts)} tone="bad" hint="Beleg nachreichen, sonst fehlt der Nachweis" />
        <Link className="dbtn small" to="/desktop/belege/ausgaben?fehlt=1">
          Fehlende Belege ansehen
        </Link>
      </div>
    );
  }
  if (id === "monate") {
    const max = Math.max(1, ...d.months.flatMap((m) => [m.revenueCents, m.incomeCents, m.expensesCents]));
    return (
      <div>
        <div className="bk-bars" role="img" aria-label="Umsatz, Einnahmen und Ausgaben je Monat">
          {d.months.map((m) => (
            <div key={m.month} className="bk-col" title={`${MONTHS[m.month - 1]}: Umsatz ${formatCents(m.revenueCents)}, Einnahmen ${formatCents(m.incomeCents)}, Ausgaben ${formatCents(m.expensesCents)}`}>
              <span className="r" style={{ height: `${Math.max(0, (m.revenueCents / max) * 100)}%` }} />
              <span className="i" style={{ height: `${Math.max(0, (m.incomeCents / max) * 100)}%` }} />
              <span className="e" style={{ height: `${Math.max(0, (m.expensesCents / max) * 100)}%` }} />
              <small>{MONTHS[m.month - 1]}</small>
            </div>
          ))}
        </div>
        <p className="bk-legend">
          <i className="r" /> Umsatz <i className="i" /> Einnahmen <i className="e" /> Ausgaben
        </p>
      </div>
    );
  }
  if (id === "zahlungen") {
    return d.recentPayments.length === 0 ? (
      <p className="muted-s">Noch keine Zahlung eingetragen. Unter „Rechnungen“ tragen Sie ein, wenn Geld eingegangen ist.</p>
    ) : (
      <ul className="dc-log">
        {d.recentPayments.map((p, i) => (
          <li key={i}>
            {dateText(p.date)} · <b>{formatCents(p.amountCents)}</b> von {p.customerName} zu {p.invoiceNumber}
          </li>
        ))}
      </ul>
    );
  }
  if (id === "kunden") {
    return d.topCustomers.length === 0 ? (
      <p className="muted-s">Noch kein Umsatz in diesem Jahr.</p>
    ) : (
      <ul className="dc-log">
        {d.topCustomers.map((c) => (
          <li key={c.name}>
            {c.name} · <b>{formatCents(c.revenueCents)}</b>
          </li>
        ))}
      </ul>
    );
  }
  if (id === "grenzen" && d.limits) {
    const cur = d.revenueCents;
    const prevPct = Math.min(100, (d.limits.previousYearCents / d.limits.previousLimitCents) * 100);
    const curPct = Math.min(100, (cur / d.limits.currentLimitCents) * 100);
    return (
      <div className="bk-limits">
        <p className="muted-s">Kleinunternehmer bleiben, wenn der Umsatz im Vorjahr unter 25.000 Euro lag und im laufenden Jahr unter 100.000 Euro bleibt.</p>
        <div className="bk-meter" aria-label="Vorjahr">
          <span style={{ width: `${prevPct}%` }} />
        </div>
        <small>
          Vorjahr: {formatCents(d.limits.previousYearCents)} von {formatCents(d.limits.previousLimitCents)}
        </small>
        <div className={`bk-meter${curPct > 80 ? " warn" : ""}`} aria-label="Laufendes Jahr">
          <span style={{ width: `${curPct}%` }} />
        </div>
        <small>
          Dieses Jahr: {formatCents(cur)} von {formatCents(d.limits.currentLimitCents)}
        </small>
        {(d.limits.previousYearCents > d.limits.previousLimitCents || curPct > 80) && (
          <p className="app-msg">Bitte sprechen Sie mit Ihrem Steuerberater: Die Grenze für Kleinunternehmer ist erreicht oder fast erreicht.</p>
        )}
      </div>
    );
  }
  return null;
}

// ---------- Ausgaben ----------

export function ExpensesView({ me }: { me: MeResponse }) {
  const onAuth = useUnauthorizedRedirect();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState<number | "all">(thisYear);
  const [missing, setMissing] = useState(() => new URLSearchParams(window.location.search).get("fehlt") === "1");
  const [query, setQuery] = useState("");
  const [data, setData] = useState<ExpenseListResponse | null>(null);
  const [error, setError] = useState("");
  const [edit, setEdit] = useState<ExpenseInfo | "new" | null>(null);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (year !== "all") p.set("year", String(year));
    if (missing) p.set("missing", "1");
    if (query.trim()) p.set("q", query.trim());
    api
      .get<ExpenseListResponse>(`/api/expenses?${p}`)
      .then((d) => {
        setData(d);
        setError("");
      })
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [year, missing, query, onAuth]);
  useEffect(() => {
    const t = setTimeout(load, query ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, query]);

  if (me.user.role === "employee") return <p className="cu-empty">Ausgaben sehen nur Inhaber und Admins.</p>;
  return (
    <section className="cu-panel">
      <div className="cu-bar">
        <div className="cu-roles" role="tablist" aria-label="Jahr">
          {[thisYear, thisYear - 1, "all" as const].map((y) => (
            <button key={y} className={year === y ? "on" : undefined} onClick={() => setYear(y)}>
              {y === "all" ? "Alle Jahre" : y}
            </button>
          ))}
        </div>
        <label className="dsearch cu-search">
          <Icon name="search" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Lieferant, Nummer, Zweck …" aria-label="Ausgaben suchen" />
        </label>
        <button className="dbtn" onClick={() => setEdit("new")}>
          <Icon name="plus" /> Ausgabe erfassen
        </button>
      </div>
      <div className="cu-hint">
        <p>
          {data && data.missingReceipts > 0 ? (
            <>
              <b>{data.missingReceipts} Ausgabe{data.missingReceipts === 1 ? "" : "n"} ohne Beleg.</b> Jede Ausgabe braucht einen Beleg, sonst fehlt der Nachweis.
            </>
          ) : (
            "Zu allen Ausgaben liegt ein Beleg vor."
          )}
        </p>
        <label className="cu-check">
          <input type="checkbox" checked={missing} onChange={(e) => setMissing(e.target.checked)} /> nur ohne Beleg
        </label>
      </div>
      <Message>{error}</Message>
      {data === null && !error && <p className="muted-s">Lädt …</p>}
      {data?.items.length === 0 && <p className="cu-empty">Keine Ausgaben gefunden.</p>}
      {data && data.items.length > 0 && (
        <table className="cu-table">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Lieferant</th>
              <th>Zweck</th>
              <th>Kategorie</th>
              <th className="r">Betrag</th>
              <th>Beleg</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((e) => (
              <tr key={e.id} onClick={() => setEdit(e)} tabIndex={0} onKeyDown={(ev) => ev.key === "Enter" && setEdit(e)}>
                <td>{dateText(e.expenseDate)}</td>
                <td>
                  {e.supplier}
                  {e.receiptNumber && <small className="cu-role">Nr. {e.receiptNumber}</small>}
                </td>
                <td>{e.purpose}</td>
                <td>{CATEGORY_LABEL[e.category] ?? e.category}</td>
                <td className="r mono">{formatCents(e.amountCents)}</td>
                <td>
                  <span className={`dc-state ${e.hasReceipt ? "paid" : "overdue"}`}>
                    {e.booked && <Icon name="lock" />}
                    {e.booked ? "Verbucht" : e.hasReceipt ? "Beleg da" : "Beleg fehlt"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {data && data.items.length > 0 && <p className="muted-s">Summe der angezeigten Ausgaben: {formatCents(data.totalCents)} (brutto)</p>}
      {edit && (
        <ExpenseSheet
          expense={edit === "new" ? null : edit}
          onClose={() => setEdit(null)}
          onChanged={() => {
            setEdit(null);
            load();
          }}
        />
      )}
    </section>
  );
}

function ExpenseSheet(props: { expense: ExpenseInfo | null; onClose: () => void; onChanged: () => void }) {
  const [e, setE] = useState<ExpenseInfo | null>(props.expense);
  const [date, setDate] = useState(e?.expenseDate ?? new Date().toISOString().slice(0, 10));
  const [supplier, setSupplier] = useState(e?.supplier ?? "");
  const [number, setNumber] = useState(e?.receiptNumber ?? "");
  const [amount, setAmount] = useState(e ? centsToText(e.amountCents) : "");
  const [vat, setVat] = useState<0 | 7 | 19>((e?.vatRate as 0 | 7 | 19) ?? 19);
  const [category, setCategory] = useState(e?.category ?? "material");
  const [purpose, setPurpose] = useState(e?.purpose ?? "");
  const [paidOn, setPaidOn] = useState(e?.paidOn ?? "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const locked = e?.booked ?? false;

  async function save(ev?: FormEvent): Promise<ExpenseInfo | null> {
    ev?.preventDefault();
    const cents = eurosToCents(amount);
    if (cents === null || cents === 0) {
      setError("Bitte tragen Sie den Betrag wie 119,00 ein (Gesamtbetrag mit Umsatzsteuer).");
      return null;
    }
    setBusy(true);
    setError("");
    const body = { expenseDate: date, supplier, receiptNumber: number, amountCents: cents, vatRate: vat, category, purpose, paidOn: paidOn || null };
    try {
      const saved = e ? await api.put<ExpenseInfo>(`/api/expenses/${e.id}`, body) : await api.post<ExpenseInfo>("/api/expenses", body);
      setE(saved);
      setBusy(false);
      return saved;
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
      return null;
    }
  }

  async function attach(file: File) {
    const saved = e ?? (await save());
    if (!saved) return;
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      setE(await upload<ExpenseInfo>(`/api/expenses/${saved.id}/receipt`, fd));
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  async function book() {
    if (!window.confirm("Ausgabe verbuchen? Danach lässt sie sich nicht mehr ändern oder löschen.")) return;
    const saved = await save();
    if (!saved) return;
    try {
      setE(await api.post<ExpenseInfo>(`/api/expenses/${saved.id}/book`));
      props.onChanged();
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function remove() {
    if (!e || !window.confirm("Diese Ausgabe löschen?")) return;
    try {
      await api.del(`/api/expenses/${e.id}`);
      props.onChanged();
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <Sheet title={e ? "Ausgabe" : "Ausgabe erfassen"} onClose={props.onClose}>
      <form className="cu-form" onSubmit={async (ev) => (await save(ev)) && props.onChanged()}>
        {locked && <p className="dc-lock wide"><Icon name="lock" /> Diese Ausgabe ist verbucht und kann nicht mehr geändert werden.</p>}
        <label>
          Datum auf dem Beleg
          <input type="date" value={date} onChange={(ev) => setDate(ev.target.value)} disabled={locked} required />
        </label>
        <label>
          Belegnummer
          <input value={number} onChange={(ev) => setNumber(ev.target.value)} maxLength={80} disabled={locked} />
        </label>
        <label className="wide">
          Von wem (Firma)
          <input value={supplier} onChange={(ev) => setSupplier(ev.target.value)} maxLength={160} disabled={locked} required autoFocus />
        </label>
        <label>
          Gesamtbetrag in Euro
          <input value={amount} onChange={(ev) => setAmount(ev.target.value)} inputMode="decimal" disabled={locked} required />
        </label>
        <label>
          Umsatzsteuer im Betrag
          <select value={vat} onChange={(ev) => setVat(Number(ev.target.value) as 0 | 7 | 19)} disabled={locked}>
            <option value={19}>19 %</option>
            <option value={7}>7 %</option>
            <option value={0}>0 % (keine)</option>
          </select>
        </label>
        <label>
          Kategorie
          <select value={category} onChange={(ev) => setCategory(ev.target.value)} disabled={locked}>
            {EXPENSE_CATEGORIES.map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Bezahlt am (freiwillig)
          <input type="date" value={paidOn} onChange={(ev) => setPaidOn(ev.target.value)} disabled={locked} />
        </label>
        <label className="wide">
          Zweck und Verwendung
          <input value={purpose} onChange={(ev) => setPurpose(ev.target.value)} maxLength={300} disabled={locked} placeholder="z. B. Kabel für Neubau Schneider" />
        </label>

        <div className="wide bk-receipt">
          {e?.hasReceipt ? (
            <p>
              <span className="dc-state paid">Beleg vorhanden</span> {e.receiptName}{" "}
              <a href={`/api/expenses/${e.id}/receipt?ansehen=1`} target="_blank" rel="noreferrer">
                ansehen
              </a>
            </p>
          ) : (
            <p>
              <span className="dc-state overdue">Beleg fehlt</span> Foto oder PDF anhängen.
            </p>
          )}
          {!locked && (
            <label className="dbtn ghost small bk-file">
              <Icon name="camera" /> {e?.hasReceipt ? "Beleg ersetzen" : "Beleg hochladen"}
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                hidden
                onChange={(ev) => {
                  const f = ev.target.files?.[0];
                  ev.target.value = "";
                  if (f) void attach(f);
                }}
              />
            </label>
          )}
        </div>
        <div className="wide">
          <Message>{error}</Message>
        </div>
        <div className="cu-buttons wide">
          {e && !locked && (
            <button type="button" className="dbtn ghost danger" onClick={() => void remove()}>
              Löschen
            </button>
          )}
          {e && !locked && e.hasReceipt && (
            <button type="button" className="dbtn ghost" onClick={() => void book()} disabled={busy}>
              <Icon name="lock" /> Verbuchen
            </button>
          )}
          <button type="button" className="dbtn ghost" onClick={props.onClose}>
            {locked ? "Schließen" : "Abbrechen"}
          </button>
          {!locked && (
            <button className="dbtn" disabled={busy}>
              Speichern
            </button>
          )}
        </div>
      </form>
    </Sheet>
  );
}
